import { useState, useEffect } from "react";
import { Process, Service, CreateProcessRequest } from "../../../api/processes";
import { ProcessSection } from "../../../api/process-sections";
import {
  LocalStage,
  LocalReasonSolution,
  EditingStage,
  EditingReasonSolution,
  ProcessFormData,
  ProcessFormErrors,
} from "../types";

export const useProcessForm = (
  process?: Process | null,
  services: Service[] = [],
  processSections: ProcessSection[] = []
) => {
  // Form Data State
  const [formData, setFormData] = useState<ProcessFormData>({
    title: "",
    serviceRef: 0,
  });

  const [errors, setErrors] = useState<ProcessFormErrors>({});
  const [selectedSections, setSelectedSections] = useState<number[]>([]);
  const [stages, setStages] = useState<LocalStage[]>([]);
  const [reasonSolutions, setReasonSolutions] = useState<LocalReasonSolution[]>(
    []
  );
  const [sectionVisibility, setSectionVisibility] = useState<{
    [sectionId: number]: boolean;
  }>({});

  // Editing States
  const [editingStage, setEditingStage] = useState<EditingStage>({
    sectionId: null,
    stageId: null,
    stageNumber: 1,
    title: "",
    isPublic: true,
    followUpGroupIdsJson: "",
  });

  const [editingReasonSolution, setEditingReasonSolution] =
    useState<EditingReasonSolution>({
      stageId: null,
      reasonSolutionId: null,
      row: 1,
      isPrimary: false,
      reason: "",
      solution: "",
      previousReasonIdsJson: "",
    });

  // Error States
  const [stageError, setStageError] = useState<string>("");
  const [reasonSolutionError, setReasonSolutionError] = useState<string>("");

  // Initialize form data when process changes
  useEffect(() => {
    if (process) {
      setFormData({
        title: process.title,
        serviceRef: process.serviceRef,
      });

      // Load existing data for editing
      setSelectedSections(process.selectedSections || []);

      // Convert API stages to local stages format
      const localStages: LocalStage[] = (process.stages || []).map((stage) => ({
        id: `stage-${stage.id}`,
        sectionId: stage.sectionRef,
        stageNumber: stage.stageNumber,
        title: stage.title,
        isPublic: (stage as any).isPublic || true,
        followUpGroupIdsJson: (stage as any).followUpGroupIdsJson || "",
      }));
      setStages(localStages);

      // Reset editing states when process changes
      setEditingStage({
        sectionId: null,
        stageId: null,
        stageNumber: 1,
        title: "",
        isPublic: true,
        followUpGroupIdsJson: "",
      });
      setEditingReasonSolution({
        stageId: null,
        reasonSolutionId: null,
        row: 1,
        isPrimary: false,
        reason: "",
        solution: "",
        previousReasonIdsJson: "",
      });
      setStageError("");
      setReasonSolutionError("");

      // Convert API reason solutions to local format
      const localReasonSolutions: LocalReasonSolution[] = (
        process.reasonSolutions || []
      ).map((rs) => ({
        id: `rs-${rs.id}`,
        stageId: `stage-${rs.stageRef}`,
        stageRef: rs.stageRef.toString(),
        row: rs.row,
        isPrimary: rs.isPrimary,
        reason: rs.reason,
        solution: rs.solution,
        previousReasonIdsJson: rs.previousReasonIdsJson,
      }));
      setReasonSolutions(localReasonSolutions);
    } else {
      setFormData({
        title: "",
        serviceRef: 0,
      });
      setSelectedSections([]);
      setStages([]);
      setReasonSolutions([]);
    }
    setErrors({});
  }, [process]);

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: ProcessFormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "عنوان فرایند الزامی است";
    }

    if (!formData.serviceRef || formData.serviceRef === 0) {
      newErrors.serviceRef = "انتخاب خدمت الزامی است";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form change handler
  const handleChange =
    (field: keyof ProcessFormData) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
    ) => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: field === "serviceRef" ? parseInt(value) || 0 : value,
      }));

      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    };

  // Section toggle handler
  const handleSectionToggle = (sectionId: number) => {
    setSelectedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Section deselect handler (with cleanup)
  const handleSectionDeselect = (sectionId: number) => {
    // Remove section from selected sections
    setSelectedSections((prev) => prev.filter((id) => id !== sectionId));

    // Remove all stages related to this section
    setStages((prev) => prev.filter((stage) => stage.sectionId !== sectionId));

    // Remove all reason solutions related to stages of this section
    setReasonSolutions((prev) => {
      const stagesToRemove = stages
        .filter((stage) => stage.sectionId === sectionId)
        .map((stage) => stage.id);

      return prev.filter((rs) => !stagesToRemove.includes(rs.stageId));
    });
  };

  // Get stages for a specific section
  const getStagesForSection = (sectionId: number) => {
    const sectionStages = stages
      .filter((stage) => stage.sectionId === sectionId)
      .sort((a, b) => a.stageNumber - b.stageNumber);

    return sectionStages;
  };

  // Get reason solutions for a specific stage
  const getReasonSolutionsForStage = (stageId: string) => {
    return reasonSolutions
      .filter((rs) => rs.stageId === stageId)
      .sort((a, b) => a.row - b.row);
  };

  // Toggle section visibility
  const handleToggleSectionVisibility = (sectionId: number) => {
    setSectionVisibility((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId], // Toggle visibility
    }));
  };

  return {
    // State
    formData,
    errors,
    selectedSections,
    stages,
    reasonSolutions,
    sectionVisibility,
    editingStage,
    editingReasonSolution,
    stageError,
    reasonSolutionError,

    // Setters
    setFormData,
    setErrors,
    setSelectedSections,
    setStages,
    setReasonSolutions,
    setSectionVisibility,
    setEditingStage,
    setEditingReasonSolution,
    setStageError,
    setReasonSolutionError,

    // Handlers
    validateForm,
    handleChange,
    handleSectionToggle,
    handleSectionDeselect,
    handleToggleSectionVisibility,
    getStagesForSection,
    getReasonSolutionsForStage,
  };
};
